'use client'

import { useEffect, useContext } from 'react'
import { Cell, Divider, Spacer, Text } from '@vinted/web-ui'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ShippingDetailsContext } from '@marketplace-web/item-page/item-page-data'
import { viewShippingPriceEvent } from '@marketplace-web/escrow-pricing/escrow-pricing-data'
import { shippingShownPricesEvent } from '@marketplace-web/shipping/checkout-shipping-data'

import ShippingDiscountBanner from './ShippingDiscountBanner'
import { ItemPageShippingPricePluginModel } from './types'
import useShippingPrice from '../../hooks/useShippingPrice'

type Props = {
  data: ItemPageShippingPricePluginModel
}

const ItemPageShippingPricePlugin = ({ data: { itemId } }: Props) => {
  const translate = useTranslate('item.shipment_options')
  const { track } = useTracking()
  const { shippingDetails } = useContext(ShippingDetailsContext)

  const shippingPrice = useShippingPrice({
    shippingDetails: shippingDetails || undefined,
    trimTranslation: true,
  })

  useEffect(() => {
    if (!shippingDetails) return

    const trackingEvent = viewShippingPriceEvent({
      screen: 'item',
      prices: [shippingDetails.price.amount],
      itemId,
    })

    const shownShippingPriceEvent = shippingShownPricesEvent({
      itemId: itemId.toString(),
      itemPagePrice: shippingDetails.price.amount,
    })

    track(trackingEvent)
    track(shownShippingPriceEvent)
  }, [track, itemId, shippingDetails])

  if (!shippingDetails) return null

  const title = shippingDetails.isFreeShipping ? translate('free_shipping') : translate('shipping')
  const shippingPriceTitle = shippingDetails.isFreeShipping ? '' : shippingPrice

  return (
    <>
      <Divider />
      <Spacer />
      <Cell
        styling={Cell.Styling.Tight}
        title={
          <Text
            as="h3"
            text={title}
            type={Text.Type.Subtitle}
            theme="amplified"
            testId="item-shipping-banner-title"
          />
        }
        suffix={
          <Text
            as="h3"
            text={shippingPriceTitle}
            type={Text.Type.Subtitle}
            testId="item-shipping-banner-price"
          />
        }
        testId="item-shipping-banner"
      />
      {shippingDetails.discount && (
        <>
          <Spacer />
          <ShippingDiscountBanner
            deliveryType={shippingDetails.discount.deliveryType}
            discountPercentage={shippingDetails.discount.maxPercent}
          />
        </>
      )}
    </>
  )
}

export default ItemPageShippingPricePlugin
